/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { Block } from '@chameleoncreator/creator-app';
import Audio_default from 'assets/Audio.png';
import { interactionContext } from 'components/lib/TextAndImage/interactionContext';
import { InteractionContext as interactionContextFromContainerLogical } from 'components/Layouts/ContainerLogical';
import EditMarker from 'components/UI/EditMarker';
import { keyframes } from 'emotion';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import {
  Fragment as _,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import getBlockById from 'services/apiHelpers/getBlockById';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { CHAMELEON_CACHE_v1 } from 'services/imageCache';
import { unlockInteraction } from 'services/unlockInteraction';
import { useCachedResource } from 'services/useCachedResource';
import { colors, getButtonFocus } from 'styles/variables';
import Transcript from './Transcript';
import SVG from 'components/lib/SVG';
import ReactBodymovin from 'react-lottie';
import imagePlaceholder from 'assets/Character A.png';
import headphonesWoman from 'assets/animations/headphonesWoman.js';
import { HostingContext } from 'hosting/context';
import {
  audioPlayEvent,
  audioSeekEvent,
  audioPauseEvent,
  audioFinishEvent,
} from 'hosting/events';
import React from 'react';

const AUDIO_FILE_DEFAULT =
  'https://freesound.org/data/previews/542/542450_5674468-lq.mp3';

const { lPink } = colors;

const frameSelected = keyframes`
  from {
    width: 50%;
    height: 50%;
  }
  to {
    width: 75%;
    height: 75%;
  }
`;

let audioResizeDelay;

const AudioImage = ({ src, description, maxHeight }) => {
  const cachedImgSrc = useCachedResource(CHAMELEON_CACHE_v1, src);

  return cachedImgSrc ? (
    <img
      src={cachedImgSrc}
      alt={description}
      css={css({
        ...(maxHeight && {
          maxHeight: 'calc(100vh - 230px)',
        }),
      })}
    />
  ) : null;
};

const ShowTranscriptBtn = ({
  id,
  setShowTranscript,
  showTranscriptFontSize = 12,
  showTranscriptFontColor = '#000',
  showTranscriptLabel = 'Show transcript',
}) => {
  return (
    <button
      id={`show-transcript-${id}`}
      css={css`
        width: 100%;
        // position: absolute;
        // left: 50%;
        // bottom: -25px;
        font-size: ${showTranscriptFontSize}px;

        // transform: translateX(-50%);
        border: none;
        outline: none;
        background: transparent;

        text-decoration: underline;
        text-align: center;
        cursor: pointer;
        color: ${showTranscriptFontColor};
        &:focus {
          ${getButtonFocus('#2684FF')}
        }
      `}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        //

        setShowTranscript(true);
      }}
      aria-label="Show transcript"
    >
      {showTranscriptLabel}
    </button>
  );
};

type AudioProps = {
  id: string | any;
  audioSrc: string;
  imgSrc: string;
  description: string;
  useTranscript: boolean;
  showTranscriptFontSize: number;
  showTranscriptFontColor: string;
  viewingTranscriptUnlocksInteraction: boolean;
  showTranscriptLabel: string;
  maxHeight: boolean;
  autoPlay: boolean;
  children?: any;
};

const Audio = (props: AudioProps) => {
  const { entry, init } = useIntersectionObserver(true) as any;

  const {
    id,
    audioSrc = AUDIO_FILE_DEFAULT,
    imgSrc = Audio_default,
    description = '',
    useTranscript,
    showTranscriptFontSize = 12,
    showTranscriptFontColor = '#000',
    viewingTranscriptUnlocksInteraction = false,
    showTranscriptLabel = 'Show transcript',
    maxHeight = false,
    children,
  } = props;

  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const focusedNodeId = useSelector((state: any) => {
    return state.focusedNodeId;
  });

  const selected = focusedNodeId === id;

  const context = (useContext(interactionContext) ||
    useContext(interactionContextFromContainerLogical)) as Block;

  // we should only unlock interaction when in the basic TextAndImage context
  // otherwise we might unlock interaction early. and that'd be a wrongness
  const shouldUnlockInteraction = context?.meta?.subType === 'TextAndImage';

  // let's grab the context id explicitly;
  const { id: contextId } = context || {};

  const hostingCtx = useContext(HostingContext);

  const mounted = useRef(false);
  const audioInteraction = useRef<HTMLDivElement>(null);
  const audio = useRef<any>(null);
  const audioPausedByUser = useRef<boolean>(false);
  const player = useRef(null);

  let autoPlay = props.autoPlay || false;
  if (audioPausedByUser.current) {
    autoPlay = false;
  }

  //
  let transcript;
  if (children) {
    [transcript] = children;
  }

  useLayoutEffect(() => {
    if (!mounted.current) {
      init([audioInteraction.current], {
        threshold: 0.25,
      });
    }

    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (window.dev && window.dev()) {
      return;
    }

    if (!audio.current) {
      return;
    }

    const onEnded = () => {
      console.log('audio ended');
      unlockInteraction(contextId);
    };

    audio.current.addEventListener('ended', onEnded);

    return () => {
      audio.current.removeEventListener('ended', onEnded);
    };
  }, []);

  useEffect(() => {
    if (viewingTranscriptUnlocksInteraction && showTranscript) {
      unlockInteraction(contextId);
    }
  }, [showTranscript]);

  if (window.dev && !window.dev() && entry) {
    if (entry.isIntersecting) {
      //
      if (autoPlay && !isPlaying) {
        //  && isPlaying
        audio.current.currentTime = 0;
        audio.current.play();
        //
      }
    } else {
      audio.current.pause();
      //
    }
  }

  let Img =
    imgSrc === Audio_default ? (
      <ReactBodymovin
        options={{
          loop: true,
          autoplay: true,
          prerender: true,
          renderer: 'svg',
          animationData: headphonesWoman,
        }}
      />
    ) : (
      <AudioImage
        src={imgSrc}
        description={description}
        maxHeight={maxHeight}
      />
    );

  Img = (
    <AudioImage
      src={imgSrc === Audio_default ? imagePlaceholder : imgSrc}
      description={description}
      maxHeight={maxHeight}
    />
  );

  const showTranscriptBtn = (
    <div>
      <ShowTranscriptBtn
        id={id}
        setShowTranscript={setShowTranscript}
        showTranscriptFontSize={showTranscriptFontSize}
        showTranscriptFontColor={showTranscriptFontColor}
        showTranscriptLabel={showTranscriptLabel}
      />
    </div>
  );

  return (
    <_>
      {window.dev() ? (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            left: 0px;
            top: 0px;
            width: 100%;
            height: 100%;
          `}
        >
          {Img}

          <div
            css={css({
              width: '100%',
              padding: 10,
            })}
          >
            <audio
              ref={audio}
              src={audioSrc}
              controls
              css={css({
                width: '100%',
              })}
            />
          </div>
          {useTranscript && showTranscriptBtn}
          <div
            css={css`
              position: absolute;
              display: flex;
              justify-content: center;
              align-items: center;
              left: 0px;
              top: 0px;
              width: 100%;
              height: 100%;
              pointer-events: none;
            `}
          >
            <div
              css={css`
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;

                width: 100%;
                height: 100%;

                box-shadow: ${selected
                  ? `0 0 0 1px ${lPink}`
                  : '0 0 0 1px lightGrey'};
                transition: all 0.2s;
                pointer-events: all;
                cursor: pointer;
                &:hover {
                  ${!selected && `box-shadow: 0 0 0 1px ${lPink};`}
                }

                ${selected && 'pointer-events: none;'}
              `}
              onClick={e => {
                e.stopPropagation();
                if (selected) return;
                setCurrentBlock({ id });
              }}
            >
              {!selected && <EditMarker />}
            </div>
          </div>
        </div>
      ) : (
        <div
          ref={audioInteraction}
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
          `}
        >
          {Img}

          <div
            css={css({
              width: '100%',
              padding: 10,
            })}
          >
            <audio
              ref={audio}
              src={audioSrc}
              onPlay={() => {
                setIsPlaying(true);
                //
                hostingCtx?.data(audioPlayEvent(contextId, id));
              }}
              onSeeked={() => {
                //
                hostingCtx?.data(audioSeekEvent(contextId, id));
              }}
              onPause={() => {
                audioPausedByUser.current = true;
                setIsPlaying(false);
                //
                hostingCtx?.data(audioPauseEvent(contextId, id));
              }}
              onEnded={() => {
                hostingCtx?.data(audioFinishEvent(contextId, id));
              }}
              css={css({
                width: '100%',
              })}
              autoPlay={autoPlay && entry && entry.isIntersecting}
              controls
            />
          </div>
          {useTranscript && showTranscriptBtn}
        </div>
      )}

      {showTranscript && (
        <Transcript
          onClose={() => {
            const tick = setInterval(() => {
              const showTranscriptButtonElement: any = document.querySelector(
                `#show-transcript-${id}`
              );

              if (showTranscriptButtonElement) {
                showTranscriptButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            setShowTranscript(false);
          }}
        >
          {transcript}
        </Transcript>
      )}
    </_>
  );
};

export default Audio;
